// PWA — 任务轮询与看图的纯展示 / 判定逻辑（可测，Req 7.5/8.1/8.2，任务 9.5）
//
// 把 `GET /jobs/{id}` 的响应（JobRecord + 可选 imageUrls）映射为：
//  - 是否仍需继续轮询（queued/running 继续，success/failed 终止，Req 7.5）；
//  - 面板展示模型（中文状态标签、图片列表、失败原因，Req 8.1）；
//  - 图片「适配分辨率」显示的尺寸计算（保持宽高比、不放大，Req 8.2）。
//
// 纯函数、无副作用，便于单元测试；hook 与组件复用之。

import type { JobRecord, JobStatus, PresignedImage } from "@comfy/shared";

/**
 * `GET /jobs/{id}` 的响应体：DynamoDB 中的 {@link JobRecord}，
 * success 时附带由 jobs Lambda 签发的 presigned 图片 URL 列表。
 */
export interface JobResponse extends JobRecord {
  imageUrls?: PresignedImage[];
}

/** 任务各状态的中文标签。 */
export const JOB_STATUS_LABELS: Record<JobStatus, string> = {
  queued: "排队中",
  running: "生成中",
  success: "已完成",
  failed: "生成失败",
};

/**
 * 是否为终止态（success / failed）。轮询应在终止态停止（Req 7.5）。
 */
export function isTerminal(status: JobStatus): boolean {
  return status === "success" || status === "failed";
}

/**
 * 是否应继续轮询：非终止态（queued / running）时继续（Req 7.5）。
 */
export function shouldContinuePolling(status: JobStatus): boolean {
  return !isTerminal(status);
}

/** 面板展示模型。 */
export interface JobView {
  /** 当前任务状态。 */
  status: JobStatus;
  /** 中文状态标签。 */
  label: string;
  /** 是否仍在轮询中（queued / running）。 */
  polling: boolean;
  /** 成功时可展示的图片（presigned URL），否则为空数组。 */
  images: PresignedImage[];
  /** 失败时的可读原因，否则为 null（Req 11.3 语义的展示端）。 */
  error: string | null;
}

/**
 * 依据任务响应推导展示模型（Req 7.5, 8.1）。
 * @param job 最近一次 `GET /jobs/{id}` 的响应；尚未取到时为 null。
 */
export function deriveJobView(job: JobResponse | null): JobView | null {
  if (!job) return null;
  const status = job.status;
  return {
    status,
    label: JOB_STATUS_LABELS[status],
    polling: shouldContinuePolling(status),
    images: status === "success" ? (job.imageUrls ?? []) : [],
    error: status === "failed" ? (job.error ?? "生成失败") : null,
  };
}

/** 二维尺寸（像素）。 */
export interface Size {
  width: number;
  height: number;
}

/**
 * 计算图片在给定视口内「适配分辨率」的显示尺寸（Req 8.2）。
 *
 * 保持原图宽高比，缩放到恰好容纳于视口内；**绝不放大**（缩放系数 ≤ 1），
 * 因此小于视口的原图按原始像素显示。非法 / 非正的原始尺寸返回 `{0, 0}`。
 *
 * @param natural 原图的自然像素尺寸（`img.naturalWidth/Height`）。
 * @param viewport 可用视口尺寸（如屏幕宽 - 内边距，屏幕高的一定比例）。
 */
export function fitToViewport(natural: Size, viewport: Size): Size {
  const nw = natural.width;
  const nh = natural.height;
  if (!(nw > 0) || !(nh > 0)) {
    return { width: 0, height: 0 };
  }
  const vw = Math.max(0, viewport.width);
  const vh = Math.max(0, viewport.height);
  // 取三者最小：不超过视口宽、不超过视口高、且不放大（≤1）。
  const scale = Math.min(1, vw / nw, vh / nh);
  return { width: Math.round(nw * scale), height: Math.round(nh * scale) };
}

// ── 保存图片与取图失败重试的纯逻辑（Req 8.3, 8.4，任务 9.6） ──────────────

/**
 * 从 presigned URL 推导用于 `<a download>` 的文件名（Req 8.3）。
 *
 * 取 URL 路径的最后一段（去除 query string 与 presigned 查询参数），
 * 若无法解析出合法带扩展名的文件名则回落为 `comfyui-{index + 1}.png`。
 *
 * @param url presigned 原图 URL。
 * @param index 图片在结果列表中的序号（用于回落命名）。
 */
export function deriveDownloadName(url: string, index: number): string {
  const fallback = `comfyui-${index + 1}.png`;
  if (typeof url !== "string" || url.length === 0) return fallback;
  // 去除 query / fragment，只取路径部分。
  const path = url.split(/[?#]/, 1)[0] ?? "";
  const segments = path.split("/");
  const last = segments[segments.length - 1] ?? "";
  const decoded = safeDecode(last);
  // 需为「非空且含扩展名」的文件名，否则回落。
  return /^[^/]+\.[A-Za-z0-9]+$/.test(decoded) ? decoded : fallback;
}

function safeDecode(s: string): string {
  try {
    return decodeURIComponent(s);
  } catch {
    return s;
  }
}

/**
 * 在 URL 上追加缓存破坏（cache-bust）查询参数以强制 `<img>` 重新加载（Req 8.4）。
 *
 * 用于「重试」时绕过浏览器 / CDN 缓存，重新拉取同一 presigned URL。
 * 依据 URL 是否已含 `?` 选择 `?` 或 `&` 连接符。
 *
 * @param url 原始 URL。
 * @param nonce 单调递增的重试计数（每次重试递增以产生不同 URL）。
 */
export function withCacheBust(url: string, nonce: number): string {
  if (typeof url !== "string" || url.length === 0) return url;
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}_retry=${nonce}`;
}

/** 取图失败后的重试策略。 */
export type RetryStrategy = "reload" | "refresh";

/**
 * 依据已失败次数决定重试策略（Req 8.4）。
 *
 * - 首次失败（`failedAttempts <= 1`）：多为瞬时网络问题，直接重载原图
 *   （附加缓存破坏参数）；
 * - 再次失败：presigned URL 可能已过期，先调用 `useJobPolling.refresh()`
 *   重取任务以获得新的 presigned URL，再重载。
 *
 * @param failedAttempts 到目前为止的失败次数（≥1）。
 */
export function decideRetryStrategy(failedAttempts: number): RetryStrategy {
  return failedAttempts <= 1 ? "reload" : "refresh";
}

/**
 * 是否具备 Web Share 能力（`navigator.share` 存在，Req 8.3）。
 *
 * 仅做存在性特征检测，桌面等缺失该 API 的浏览器返回 false（不崩溃）。
 */
export function hasWebShare(
  nav: { share?: unknown } | undefined | null,
): boolean {
  return !!nav && typeof nav.share === "function";
}

/**
 * 是否可通过 Web Share API 分享图片文件到系统相册 / 分享面板（Req 8.3）。
 *
 * 需同时满足：`navigator.share` 与 `navigator.canShare` 均可用，且
 * `canShare({ files })` 对给定文件返回 true。任一缺失或抛错均返回 false，
 * 以便在不支持的浏览器上安全回落到 `<a download>`。
 *
 * @param nav navigator 形态对象（便于测试注入）。
 * @param files 待分享的文件列表。
 */
export function canShareImageFiles(
  nav:
    | { share?: unknown; canShare?(data?: unknown): boolean }
    | undefined
    | null,
  files: File[],
): boolean {
  if (!hasWebShare(nav)) return false;
  if (!nav || typeof nav.canShare !== "function") return false;
  if (!Array.isArray(files) || files.length === 0) return false;
  try {
    return nav.canShare({ files });
  } catch {
    return false;
  }
}
